import {createContext, useContext} from 'react';
import type {UsersMeResponseType} from '../../repositories/usersMeRepository';

export type UserType = UsersMeResponseType;

/**
 * 認証状態を表す型
 * @description
 * - 初期値: initializing (Hydrationエラー防止のため)
 * - トークンがCookieに存在 -> ユーザー情報を取得中 -> loading
 * - ユーザー情報を取得できた -> authenticated
 * - ユーザー情報を取得できなかった -> error
 * - トークンがCookieに存在しない -> unauthenticated
 *
 * ## possiblyAuthenticated
 * クッキーにトークンが存在している時にtrueになる。実際にそれが有効かは分からない。
 * ログイン済みであることを、実際にユーザー情報をDBから取得する前に知りたい場合に使う。
 * レイアウトシフトを軽減することができるが、実際にDBから取得できなかった場合はfalseになる。
 * そのため、データのミューテーションではstatusを使うべき。
 *
 * ## 旧`useAuth`との対応
 * - isSignedIn <-> status === 'authenticated'
 * - isValid <-> possiblyAuthenticated
 * - isIdle <-> status === 'loading'
 */
export type AuthStatus =
  | 'initializing'
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'error';

export type AuthAction = {
  signOut: () => Promise<void>;
};

type AuthStateBase = {
  user: UserType | undefined;
  accessToken: string | undefined;
  status: AuthStatus;
  possiblyAuthenticated: boolean;
};
type Initializing = AuthStateBase & {
  user: undefined;
  isGraduatedUser: false;
  accessToken: undefined;
  status: 'initializing';
  possiblyAuthenticated: boolean;
};
type Loading = AuthStateBase & {
  user: undefined;
  isGraduatedUser: false;
  accessToken: string;
  status: 'loading';
  possiblyAuthenticated: true;
};
type Authenticated = AuthStateBase & {
  user: UserType;
  isGraduatedUser: boolean;
  accessToken: string;
  status: 'authenticated';
  possiblyAuthenticated: true;
};
type Unauthenticated = AuthStateBase & {
  user: undefined;
  isGraduatedUser: false;
  accessToken: undefined;
  status: 'unauthenticated';
  possiblyAuthenticated: false;
};
type ErrorState = AuthStateBase & {
  user: undefined;
  isGraduatedUser: false;
  accessToken: string | undefined;
  status: 'error';
  possiblyAuthenticated: boolean;
};

export type AuthState =
  | Initializing
  | Loading
  | Authenticated
  | Unauthenticated
  | ErrorState;

export const defaultAuthState: AuthState = {
  user: undefined,
  isGraduatedUser: false,
  accessToken: undefined,
  status: 'initializing',
  possiblyAuthenticated: false,
};

export const mockSignOut = async () => {
  console.log('signOut() called but not implemented');
};

export const defaultAuthAction: AuthAction = {
  signOut: mockSignOut,
};

export type AuthContextType = AuthState & AuthAction;
export const defaultAuthContext: AuthContextType = {
  ...defaultAuthState,
  ...defaultAuthAction,
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const useAuth = () => useContext(AuthContext);
